import { LitElement, html, unsafeHTML, css, classMap } from 'lit-all';
import chunk from 'lodash-chunk'

class AsSizeGuide extends LitElement {
  static properties = {
    activeVariantId: {
      type: Number,
      attribute: 'data-active-variant-id'
    },
    variantData: {
      type: Object,
    },
    itemsPerRow: {
      type: Number,
    },
    showMoreDiagrams: {
      type: Boolean,
    }
  }

  get activeVariantData() {
    return this.variantData?.find(({ id }) => id === this.activeVariantId )
  }

  get chartData() {
    if (!this.activeVariantData) return null

    return Object.keys(this.activeVariantData.chart).map(key => this.activeVariantData.chart[key])
  }

  get sortedVariants () {
    return this.variantData?.sort((a,b) => a.chart.insideLength.in - b.chart.insideLength.in)
  }

  /**
 * Computes and returns the width percentages for diagram elements based on their normalized sizes.
 * This getter method organizes diagram elements into groups, calculates their sizes relative to the first row,
 * and adjusts their widths to fit within a maximum container space, ensuring all widths are expressed as percentages.
 *
 * @returns {Array<Array<string>>|null} An array of arrays containing width percentages for each diagram element,
 *                                      or null if sortedVariants are not available.
 */
  get diagramGroups() {
    if (!this.sortedVariants || !this.diagrams.length) return null

    const groupedVariants = this.groupVariantsByOption(this.sortedVariants)
    const optionDiagrams = Object.keys(groupedVariants).map((key) => groupedVariants[key][0])
    const diagramGroups = chunk(optionDiagrams, this.itemsPerRow)

    const firstRow = this.itemsPerRow === 2
      ? diagramGroups.slice(0, 2).flat()
      : diagramGroups[0]

    const firstRowSize = firstRow.reduce((total, el) => total + el.chart.insideLength.in, 0);
    const mean = firstRowSize / firstRow.length;
    const baseWidthPercentage = this.maxContainerSpace / firstRowSize;

    const normalizedWidths = diagramGroups.map(group => this.normalizeDiagramSize(group, firstRowSize, mean))
    return diagramGroups.map((group, i) => {
      return group.map((el, j) => {
        // const multiplier = baseWidthPercentage * (4 / 4);
        const size = normalizedWidths[i][j] * baseWidthPercentage - 28
        const width = Math.min(size, this.maxContainerSpace)
        return `${(width / this.maxContainerSpace * 100)}%`
      })
    });
  }

  get initialDiagrams() {
    if (this.parentNode.offsetWidth <= 600) {
      return this.diagramGroups.slice(0, 2);
    }

    return this.diagramGroups.slice(0, 1);
  }

  get bodyCopy() {
    return this.getAttribute('data-body-copy')
  }

  constructor() {
    super();

    this.showMoreDiagrams = false
    this.maxContainerSpace = this.parentNode.offsetWidth
    this.itemsPerRow = this.maxContainerSpace <= 540 ? 2 : this.maxContainerSpace >= 780 ? 5 : 4
  }

  groupVariantsByOption(variants) {
    return variants.reduce((acc, item) => {
      const key = item.option1;
      if (!acc[key]) {
          acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {})
  }

  normalizeDiagramSize(diagramWidths, totalSum, mean) {
    let adjustedSum = 0;

    // Step 1: Adjust values towards the mean
    const adjustedValues = diagramWidths.map(({chart }) => {
      // Calculate the adjustment factor based on distance from the mean
      const distance = chart.insideLength.in - mean;
      // Reduce the distance to the mean by a certain factor (e.g., half the distance)
      const adjustedValue = mean + distance * 0.5;
      adjustedSum += adjustedValue;

      return adjustedValue;
    });

    // Step 2: Scale adjusted values to maintain the original total sum
    const scale = totalSum / adjustedSum;
    const normalizedValues = adjustedValues.map(value => diagramWidths.length < 4 ? value : value * scale);

    return normalizedValues;
  }

  handleClick() {
    this.showMoreDiagrams = !this.showMoreDiagrams
  }

  isDiagramActive(label) {
    const text = label.replace(/"$/, '');
    const optionName  = this.activeVariantData.option1.replace(/"$/, '');

    return text == optionName
  }

  renderSizeChart() {
    if (!this.chartData) return null

    return html`
      <div class="size-chart">
        <header class="size-chart__header">
          <div class="size-chart__item">Measurements</div>
          <div class="size-chart__item">Inches</div>
          <div class="size-chart__item">Centimeters</div>
        </header>
        ${
            this.chartData.map(row => {
              return html`
                <div class="size-chart__item">${row.label}</div>
                <div class="size-chart__item">${row.in != 'N/A' ? `${row.in}"` : 'N/A'}</div>
                <div class="size-chart__item">${row.cm != 'N/A' ? `${row.cm} cm` : 'N/A'}</div>
              `
            })
        }
      </div>
    `
  }

  renderCaseDiagrams() {
    if (!this.diagramGroups || !this.diagrams.length) return null

    const diagramsToShow = this.showMoreDiagrams ? this.diagramGroups : this.initialDiagrams

    return html`
      <div class="container ${classMap({'container--border': this.activeVariantData?.description})}" >
        ${
          diagramsToShow.map((group, i) => html`
            <div class="image-group ${classMap({'image-group--single': group.length === 1 && i >= 1 && this.itemsPerRow === 4 })}">
              ${group.map((width, j) => {
                const index = (i * this.itemsPerRow) + j;
                const {image, label} = this.diagrams[index]

                return html`
                  <div class="item" style="width: ${width}">
                    <div class="svg-wrapper ${classMap({active: this.isDiagramActive(label)})}">
                      <img src="${image}"/>
                    </div>
                    <span class="item__title" style="margin: 0">${label}</span>
                  </div>
                `
              })}
            </div>
          `)
        }
    </div>
    `;
  }

  render() {
    return html`
      <p>${this.bodyCopy}</p>

      ${this.renderSizeChart()}

      ${unsafeHTML(this.activeVariantData?.description)}

      ${this.renderCaseDiagrams()}

      ${
        this.diagramGroups?.length > 1 ?
          html `
            <footer>
              <button
                @click="${this.handleClick}"
                type="button" class="product-form__drawings-download button button--secondary"
                aria-expanded="${this.showMoreDiagrams ? 'true' : 'false'}"
                aria-label="${this.showMoreDiagrams ? 'Show less' : 'Show more'}">
                ${this.showMoreDiagrams ? 'Show Less' : 'Show More' }
              </button>
            </footer>
          ` : ``
      }
    `

  }

  static get styles() {
    return css`
      .container {
        display: flex;
        flex-wrap: wrap;
        gap: 28px; /* Gap between image groups */
        margin-top: 20px;
        padding-top: 20px;
      }

      .container--border {
        border-top: 1px solid black;
        padding-top: 30px;
      }

      .image-group {
        display: flex;
        flex-wrap: nowrap;
        justify-content: space-around;
        width: 100%; /* Ensure each group takes full width */
        align-items: baseline;
      }

      .image-group--single {
        justify-content: space-between;
      }


      .svg-wrapper {
        display: inline-block;
        overflow: hidden;
        position: relative;
        border-bottom: 1px dashed #666;
        padding-bottom: 15px;
        margin-bottom: 15px;
        width: 100%;
        opacity: .55;
      }

      .svg-wrapper img {
        width: 100%; /* Make the SVGs fill their container */
        height: auto; /* Maintain aspect ratio */
        opacity: .55
      }

      .svg-wrapper.active img {
        opacity: 1;
        mix-blend-mode: difference;
      }

      .item {
        display: flex;
        flex-direction: column;
        justify-content: end;
        align-items: center;
      }

      .item__title {
        margin: 0;
        font-size: 12px;
        font-weight: 400;
        line-height: 14px;
        text-align: center;
      }

      .size-chart {
        border-top: 1px solid #666;
        border-left: 1px solid #666;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(7, 1fr);
        grid-template-areas:
          "header header header"
          "body body body";
      }

      .size-chart__header {
        grid-area: header;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
      }

      .size-chart__header .size-chart__item {
        font-weight: bold;
      }

      .size-chart__item {
        text-align: center;
        font-family: "Work Sans";
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 14px; /* 116.667% */
        padding: 10px;
        border-right: 1px solid #666;
        border-bottom: 1px solid #666;
      }

      .size-chart__item:nth-of-type(3n+1) {
        text-align: left;
        background-color: #F6F6F6;
      }

      .size-chart__description {
        border-bottom: 1px solid black;
      }

      footer {
        margin-top: 15px;
      }

      button {
        -webkit-appearance: none;
        background: none;
        border: none;
        border-radius: 0;
        padding: 0;
        padding-bottom: 5px;
        overflow: visible;
        border-bottom: 2px solid var(--Brand-Brand-Secondary, #515355);
        color: var(--Brand-Brand-Secondary, #515355);
        text-align: center;
        font-family: "Work Sans";
        font-size: 14px;
        font-weight: normal;
        line-height: 140%; /* 19.6px */
        letter-spacing: -0.28px;
        cursor: pointer;
      }


      @media screen and (max-width: 375px) {
        .size-chart__header .size-chart__item {
          padding-left: 0;
          padding-right: 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    `;
  }
}

customElements.define('as-size-guide', AsSizeGuide);
